"use client";

import { ControlButton, Controls, ReactFlowProvider, useReactFlow, useStore } from "@xyflow/react";
import { Hand, LockKeyhole, Maximize2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Canvas } from "@/components/canvas/canvas";
import { SimulatePanel } from "@/components/inspector/simulate";
import { heroZoomBand, planExampleFrame, planHeroFrame } from "@/lib/hero-frame";
import { useCableSizeVisibility } from "@/lib/settings/cable-size-visibility";
import { useFuseVisibility } from "@/lib/settings/fuse-visibility";
import { useSystemStore } from "@/lib/store";
import { useIsTouchLayout } from "@/lib/use-is-touch-layout";

/** Shared presentation for the homepage and isolated resource-example frames.
 * The caller owns the system and initial scenario; changing views never reloads it. */
export function ExampleSchematic({
  caption,
  wiringResult,
  footer,
  layout = "landing",
  embedded = true,
  ariaLabel,
  allowSimulation = true,
  showCableDetails = false,
  showControls = true,
  simulationResult,
  simulationHint = "Change the sunshine, switch a load off, or move the battery slider to see what happens.",
}: {
  caption: ReactNode;
  wiringResult: ReactNode;
  footer?: ReactNode;
  layout?: "landing" | "resource" | "portrait" | "library";
  embedded?: boolean;
  ariaLabel?: string;
  allowSimulation?: boolean;
  showCableDetails?: boolean;
  showControls?: boolean;
  simulationHint?: string;
  simulationResult?: ReactNode;
}) {
  const mode = useSystemStore((s) => s.mode);
  const setMode = useSystemStore((s) => s.setMode);
  const isTouchLayout = useIsTouchLayout();
  const [unlocked, setUnlocked] = useState(false);
  const interactionToggle = useRef<HTMLButtonElement>(null);
  const drawing = useRef<HTMLDivElement>(null);
  const library = layout === "library";
  const resource = layout === "resource" || library;
  const drawingOnly = library && mode === "edit";
  const portrait = layout === "portrait";
  const interactive = !isTouchLayout || unlocked;
  const inlineFooter = portrait && allowSimulation && mode === "simulate";

  useEffect(() => {
    if (isTouchLayout && unlocked) {
      const target = drawing.current?.querySelector<HTMLButtonElement>(".react-flow__controls-zoomin") ?? drawing.current;
      target?.focus({ preventScroll: true });
    }
  }, [isTouchLayout, unlocked, showControls]);

  useEffect(() => {
    const fuses = useFuseVisibility.getState().visible;
    const cableSizes = useCableSizeVisibility.getState().visible;
    // Transient overrides only: exploring an example must not save preferences.
    useFuseVisibility.setState({ visible: showCableDetails || (isTouchLayout && (!portrait || mode === "edit")) });
    useCableSizeVisibility.setState({ visible: showCableDetails || (isTouchLayout && (!portrait || mode === "edit")) });
    return () => {
      useFuseVisibility.setState({ visible: fuses });
      useCableSizeVisibility.setState({ visible: cableSizes });
    };
  }, [isTouchLayout, showCableDetails, portrait, mode]);

  return <figure id={resource ? undefined : "example"}
    className={resource ? "lp lp-figure flex h-full flex-col overflow-hidden" : `lp-card lp-figure scroll-mt-20 overflow-hidden${portrait ? " lp-portrait-example" : ""}`}>
    <figcaption className={`flex shrink-0 border-b border-[var(--lp-rule-soft)] text-[15px] leading-snug ${portrait || library ? "items-center justify-between gap-2 px-4 py-3 sm:px-5" : "flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6"}`}>
      <p className="text-[var(--lp-ink-2)]">{caption}</p>
      {allowSimulation && <div role="group" aria-label="Example mode" className="inline-flex flex-none self-start rounded-full border border-[var(--lp-rule)] bg-[var(--lp-paper)] p-1 sm:self-auto">
        {(["edit", "simulate"] as const).map((value) => <button key={value} type="button" aria-pressed={mode === value}
          onClick={() => setMode(value)}
          className={`min-h-11 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${mode === value ? "bg-[var(--lp-ink)] text-[var(--lp-card)]" : "text-[var(--lp-ink-2)] hover:text-[var(--lp-ink)]"}`}>
          {value === "simulate" ? "Simulate" : "Wiring"}
        </button>)}
      </div>}
    </figcaption>

    <ReactFlowProvider>
      <div className={resource
        ? drawingOnly ? "grid min-h-0 flex-1 grid-cols-1" : "grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(240px,1fr)_300px] lg:grid-cols-[minmax(0,1fr)_300px] lg:grid-rows-1"
        : portrait ? "grid grid-cols-1" : "grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px]"}>
        <div className={`relative w-full overflow-hidden bg-[var(--lp-paper)] ${resource ? "min-h-0" : portrait ? (isTouchLayout ? "aspect-[6/7] sm:aspect-auto sm:h-[576px]" : "aspect-square sm:aspect-auto sm:h-[520px]") : "aspect-[4/3] sm:aspect-[5/2]"}`}
          onKeyDown={(event) => {
            if (event.key === "Escape" && isTouchLayout && unlocked) {
              event.preventDefault();
              setUnlocked(false);
              interactionToggle.current?.focus();
            }
          }}>
          <div ref={drawing} className={`absolute inset-0 ${portrait && isTouchLayout ? "bottom-14" : ""}`} inert={!interactive}
            tabIndex={showControls ? undefined : -1} role={showControls ? undefined : "group"}
            aria-label={showControls ? undefined : ariaLabel ?? "Interactive schematic"}>
            <Canvas viewOnly hideCableLabels embedded={embedded} autoFit={false} showControls={false}
              showMetricsOnTouch={!portrait || mode === "edit"} showBatteryDetails={false} minZoom={resource ? 0.05 : undefined}
              controlsPosition="bottom-left" ariaLabel={ariaLabel} />
            <ExampleFrame wholeSystem={resource || portrait} padding={portrait ? 32 : library ? 80 : undefined}
              showControls={showControls && (resource || interactive)}
              bottomInset={resource && isTouchLayout ? 88 : undefined} />
          </div>
          {isTouchLayout && <button ref={interactionToggle} type="button" onClick={() => setUnlocked(!unlocked)}
            className={unlocked
              ? `absolute right-3 ${portrait ? "bottom-3" : "top-3"} inline-flex min-h-11 items-center gap-2 rounded-full border border-[var(--lp-rule)] bg-[var(--lp-card)] px-3 py-2 text-[13px] font-semibold`
              : "absolute inset-0 flex items-end justify-center bg-transparent pb-4"}
            aria-label={unlocked ? "Lock to scroll" : "Enable panning and zooming on the schematic"}>
            {unlocked ? <><LockKeyhole aria-hidden className="h-3.5 w-3.5" /> Lock to scroll</> :
              <span className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[var(--lp-rule)] bg-[var(--lp-card)] px-4 py-2 text-[13px] font-semibold text-[var(--lp-ink)] shadow-[var(--lp-shadow)]">
                <Hand aria-hidden className="h-3.5 w-3.5" strokeWidth={1.75} /> Tap to explore the drawing
              </span>}
          </button>}
        </div>

        {!drawingOnly && <div className={`flex min-h-0 flex-col border-t border-[var(--lp-rule-soft)] bg-[var(--lp-paper-deep)]/60 ${portrait ? "" : "lg:border-t-0 lg:border-l"} ${resource ? "overflow-y-auto" : ""}`}>
          {allowSimulation && mode === "simulate" ? <div role="region" aria-label="Simulation results">
            {simulationResult ?? <SimulatePanel />}
            {inlineFooter ? <div className="flex flex-wrap items-center gap-x-5 gap-y-3 px-5 pb-3 lg:gap-y-1">
              <p className="min-w-0 basis-full text-[13px] leading-snug text-[var(--lp-ink-2)] lg:flex-[1_1_16rem]">{simulationHint}</p>
              {footer && <div className="mx-auto shrink-0 lg:mr-0">{footer}</div>}
            </div> : <p className="px-5 pb-5 text-[13px] leading-snug text-[var(--lp-ink-2)]">{simulationHint}</p>}
          </div> : wiringResult}
          {footer && !inlineFooter && <div className={`mt-auto px-5 ${portrait ? "flex justify-center pb-3 lg:justify-end" : "pb-5"}`}>{footer}</div>}
        </div>}
      </div>
    </ReactFlowProvider>
  </figure>;
}

/** Frame from saved geometry so lazy iframes and nested zones do not race
 * React Flow's node measurements. Scenario changes leave the viewport alone. */
function ExampleFrame({ wholeSystem, padding, bottomInset, showControls }: { wholeSystem: boolean; padding?: number; bottomInset?: number; showControls: boolean }) {
  const { fitBounds } = useReactFlow();
  const paneWidth = useStore((s) => s.width);
  const paneHeight = useStore((s) => s.height);
  const system = useSystemStore((s) => s.system);

  const fitDrawing = useCallback(() => {
    if (!system.nodes.length || !paneWidth || !paneHeight) return;
    const frame = wholeSystem
      ? planExampleFrame(system, { paneWidth, paneHeight, maxZoom: 1, padding, bottomInset })
      : planHeroFrame(system, { paneWidth, paneHeight, ...heroZoomBand(paneWidth) });
    if (frame) void fitBounds(frame, { padding: 0, duration: 0 });
  }, [system, paneWidth, paneHeight, fitBounds, wholeSystem, padding, bottomInset]);
  useEffect(fitDrawing, [fitDrawing]);
  // React Flow's default fit button only includes nodes. Reuse the initial
  // frame so outer cable routes and the phone's control strip stay visible.
  return showControls ? <Controls position="bottom-left" showInteractive={false} showFitView={false}>
    <ControlButton onClick={fitDrawing} className="react-flow__controls-fitview" title="Fit View" aria-label="Fit View">
      <Maximize2 />
    </ControlButton>
  </Controls> : null;
}
